import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { LoginPhoneInput } from '@/components/login/LoginPhoneInput';
import { sendOtpSms } from '@/graphql/hasura/auth';
import { AlterError } from '@/lib/error';
import { sendSentryError } from '@/lib/sentry/sentry';
import { ErrorCode } from '@/models/error-codes';
import type { AlterPasswordlessPhoneFormData } from '@/models/login.model';
import { LoginStage } from '@/models/login.model';

interface LoginPasswordlessSendProps {
  setLoginStage: (stage: LoginStage) => void;
  setPhone: (phone: string) => void;
  className?: string;
}

export const LoginPasswordlessSend: React.FC<LoginPasswordlessSendProps> = ({
  setLoginStage,
  setPhone,
  className,
}) => {
  const [loading, setLoading] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);

  const onSubmit = async (data: AlterPasswordlessPhoneFormData) => {
    setLoading(true);

    try {
      await sendOtpSms({ phone: data.phone });
      setPhone(data.phone);
      setLoginStage(LoginStage.passwordLessConfirm);
    } catch (error) {
      if (error instanceof AlterError) {
        switch (error.errorCode) {
          case ErrorCode.errorSendingOtpSmsToPhone:
            setOtpError('Error Sending Code');
            return;
          case ErrorCode.errorFindingUserForPhone:
            setOtpError('Something went wrong. Please check the number and try again.');
            return;
          default:
            setOtpError('Something went wrong.');
        }
      }
      sendSentryError('UNKNOWN_ALTER_ERROR', error);
    } finally {
      setLoading(false);
    }
  };

  const {
    control,
    getValues,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<AlterPasswordlessPhoneFormData>({});

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="min-h-[152px]">
        <div className="flex flex-col gap-[16px] mt-6 break-words lg:mt-10">
          <LoginPhoneInput
            control={control}
            getValues={getValues}
            setValue={setValue}
            errors={errors}
          ></LoginPhoneInput>
        </div>
        <div className="h-20">
          <div className="pt-5 px-6 text-sm text-alter-black-80 font-light">
            Enter your phone number and we’ll text you a six-digit login code
          </div>
          {otpError && <div className="mt-3 text-sm text-alter-error">{otpError}</div>}
        </div>
      </div>
      <button
        type="submit"
        data-testid="loginButton"
        className="button w-full mt-6 mb-2 font-light text-lg"
      >
        {loading ? 'Loading' : 'Send Code'}
      </button>
    </form>
  );
};
